import { forwardRef, useImperativeHandle } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form, useForm } from "capitalrx-components-2.0/lib/forms";
import { Buttons } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";
import { formatDollars } from "capitalrx-utilities";

import { Textbox, ThemedButton } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { validateInteger } from "@/App/utils/validation";

import { DirectMemberReimbursementCompoundPrescriptionIngredientField } from "./DirectMemberReimbursementCompountPrescriptionIngredientField";

interface DirectMemberReimbursementCompoundPrescriptionFormProps {
  readonly smallFieldsColumns: number;
}

interface DMRCompoundPrescriptionIngredient {
  ndc: string;
  quantity: number;
  ingredient_cost: number;
}

interface DMRCompoundPrescriptionForm {
  rx_number: string;
  day_supply: number;
  ingredients: DMRCompoundPrescriptionIngredient[];
}

export type DirectMemberReimbursementCompoundPrescriptionFormHandle = {
  submit: () => Promise<DMRCompoundPrescriptionForm>;
};

const MINIMUM_INGREDIENTS = 2;
const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementCompoundPrescriptionForm";

export const DirectMemberReimbursementCompoundPrescriptionForm = forwardRef<
  DirectMemberReimbursementCompoundPrescriptionFormHandle,
  DirectMemberReimbursementCompoundPrescriptionFormProps
>(({ smallFieldsColumns }, ref) => {
  const { t: translate } = useTranslation();
  const form = useForm<DMRCompoundPrescriptionForm>({
    defaultValues: {
      ingredients: Array.from({ length: MINIMUM_INGREDIENTS }, () => ({})),
    },
  });

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise<DMRCompoundPrescriptionForm>((resolve) => {
        form.handleSubmit(
          () => resolve(form.getValues()),
          () => resolve(null),
        )();
      }),
  }));

  const {
    fields: ingredients,
    remove: removeIngredient,
    insert: insertIngredient,
  } = useFieldArray({ name: "ingredients", control: form.control });

  const watchedIngredients = useWatch({ control: form.control, name: "ingredients" });

  const total = watchedIngredients.reduce(
    (acc, item) => {
      const quantity = +item.quantity;
      const ingredientCost = +item.ingredient_cost;

      if (!Number.isNaN(quantity)) {
        acc.totalQuantity += quantity;
      }

      if (!Number.isNaN(ingredientCost)) {
        acc.totalIngredientCost += ingredientCost;
      }

      return acc;
    },
    { totalQuantity: 0.0, totalIngredientCost: 0.0 },
  );

  return (
    <Form form={form}>
      <Stack spacing="medium">
        <Subheading>{translate(`${TRANSLATION_PATH}.compoundPrescriptionsInformation`)}</Subheading>
        <Grid>
          <Grid.Span columns={smallFieldsColumns}>
            <Textbox.Field name="rx_number" label={translate(`${TRANSLATION_PATH}.rxNumber`)} required />
          </Grid.Span>
          <Grid.Break />
          <Grid.Span columns={smallFieldsColumns}>
            <Textbox.Field
              name="day_supply"
              label={translate(`${TRANSLATION_PATH}.daySupply`)}
              required
              validator={(value) => validateInteger(value, translate)}
            />
          </Grid.Span>
        </Grid>
        <Semibold>{translate(`${TRANSLATION_PATH}.ingredients`)}</Semibold>
        <Grid>
          <Grid.Span columns={smallFieldsColumns}>
            <Stack spacing="mini">
              <Body>{translate(`${TRANSLATION_PATH}.totalQuantity`)}</Body>
              <Semibold>{total.totalQuantity.toFixed(2)}</Semibold>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={smallFieldsColumns}>
            <Stack spacing="mini">
              <Body>{translate(`${TRANSLATION_PATH}.totalIngredientCost`)}</Body>
              <Semibold>{formatDollars(total.totalIngredientCost)}</Semibold>
            </Stack>
          </Grid.Span>
        </Grid>
        {ingredients.map((field, index) => (
          <DirectMemberReimbursementCompoundPrescriptionIngredientField
            index={index}
            isRemovable={index >= MINIMUM_INGREDIENTS}
            removeIngredientCallback={removeIngredient}
            key={field.id}
          />
        ))}
        <StackOrShelf>
          <ThemedButton
            appearance={Buttons.Functional.Tertiary.Large}
            icon="add"
            onClick={() => insertIngredient(ingredients.length, { ndc: null, quantity: null, ingredient_cost: null })}
          >
            {translate(`${TRANSLATION_PATH}.addIngredient`)}
          </ThemedButton>
          <Shelf.Spacer />
        </StackOrShelf>
      </Stack>
    </Form>
  );
});
